import { forwardRef } from 'react';
import { cn } from '../../lib/utils';

/**
 * PriceInput - Currency input with € symbol
 *
 * Handles euro formatting and validation.
 */
const PriceInput = forwardRef(function PriceInput(
  {
    value,
    onChange,
    label,
    error,
    helperText,
    min = 0,
    max,
    step = 1,
    placeholder = '0.00',
    disabled = false,
    size = 'md',
    className,
    ...props
  },
  ref
) {
  const sizeClasses = {
    sm: 'py-2 text-sm',
    md: 'py-3 text-base',
    lg: 'py-4 text-lg',
  };

  const iconSizeClasses = {
    sm: 'text-sm pl-7',
    md: 'text-base pl-9',
    lg: 'text-lg pl-11',
  };

  const symbolSizeClasses = {
    sm: 'text-sm left-3',
    md: 'text-base left-3',
    lg: 'text-lg left-4',
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-cream/70 mb-2">
          {label}
        </label>
      )}

      <div className="relative">
        {/* Euro symbol */}
        <span
          className={cn(
            'absolute top-1/2 -translate-y-1/2 text-cream/50 font-medium pointer-events-none',
            symbolSizeClasses[size]
          )}
        >
          €
        </span>

        <input
          ref={ref}
          type="number"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'w-full bg-midnight border rounded-lg pr-4 text-cream font-mono font-semibold placeholder:text-cream/30 transition-all outline-none',
            iconSizeClasses[size],
            sizeClasses[size],
            error
              ? 'border-danger focus:border-danger focus:ring-2 focus:ring-danger/20'
              : 'border-cream/20 focus:border-primary focus:ring-2 focus:ring-primary/20',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          {...props}
        />
      </div>

      {helperText && !error && (
        <p className="text-cream/50 text-sm mt-1.5">{helperText}</p>
      )}
      {error && <p className="text-danger text-sm mt-1.5">{error}</p>}
    </div>
  );
});

export default PriceInput;

/**
 * PriceDisplay - Formatted price display
 */
export function PriceDisplay({
  amount,
  cents = false,
  size = 'md',
  className,
}) {
  // Convert cents to euros if needed
  const euroAmount = cents ? amount / 100 : amount;

  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-xl',
    xl: 'text-2xl',
  };

  return (
    <span className={cn('font-mono font-semibold text-cream', sizeClasses[size], className)}>
      €{euroAmount.toFixed(2)}
    </span>
  );
}

/**
 * PriceRange - Display a price range
 */
export function PriceRange({ min, max, cents = false, className }) {
  const minEuro = cents ? min / 100 : min;
  const maxEuro = cents ? max / 100 : max;

  return (
    <span className={cn('font-mono text-cream', className)}>
      €{minEuro.toFixed(2)} - €{maxEuro.toFixed(2)}
    </span>
  );
}
